// src/encounter/lib/l360-activation.ts
// L360 A1-A3 view rules. Pure functions; no React, no fetch.
//   A1 compose one care plan: goals grouped by track, SIGNED apart from PROPOSED.
//   A2 accountable team: role text alone is not acceptance.
//   A3 ready to activate?: shows the Workflows A3 gate result; computes no verdict of its own.
import type { PatientEnrolmentWire } from '@/types/care-model-api';
import type {
  A3ConflictWire,
  CareModelRunWire,
  CreateTeamTaskRequest,
  PatientRevisions,
  RunHoldWire,
  RunLogEntryWire,
  RunRevisions,
  TeamMemberWire,
} from '@/types/l360-api';
import type { CarePlanView, GoalView } from './care-plan-view';

/** A1: the sourceNodeId prefixes that make a plan an L360 plan. */
const L360_NODE_PREFIXES = ['M4-', 'M5-', 'F5-', 'P5-'] as const;

export type GoalTrack = 'metabolic' | 'function' | 'lifestyle' | 'other';

export const TRACK_ORDER: readonly GoalTrack[] = ['metabolic', 'function', 'lifestyle', 'other'];
export const TRACK_LABEL: Record<GoalTrack, string> = {
  metabolic: 'Metabolic',
  function: 'Function',
  lifestyle: 'Lifestyle',
  other: 'Other',
};

export const isL360Goal = (g: GoalView): boolean => {
  const node = g.sourceNodeId;
  return !!node && L360_NODE_PREFIXES.some((p) => node.startsWith(p));
};
export const isL360Plan = (plan: CarePlanView): boolean => plan.goals.some(isL360Goal);

/** A1: the server's track when it is one we know, else the prefix of sourceNodeId before the first "-". */
export function trackOf(goal: GoalView): GoalTrack {
  const known = TRACK_ORDER.find((t) => t === goal.track);
  if (known) return known;
  const prefix = goal.sourceNodeId?.split('-')[0];
  if (prefix === 'M4' || prefix === 'M5') return 'metabolic';
  if (prefix === 'F4' || prefix === 'F5') return 'function';
  if (prefix === 'P5') return 'lifestyle';
  return 'other';
}

/** A1: SIGNED = the server sent a signoff (accepted AND set by the sign endpoint). Everything else is PROPOSED. */
export const isSigned = (g: GoalView): boolean => !!g.signoff;

/** M4: a goal sourced from the M4 node, the bare node id or any `M4-` child. */
export const isM4Goal = (g: GoalView): boolean => g.sourceNodeId === 'M4' || !!g.sourceNodeId?.startsWith('M4-');

export interface TargetsSignedEvent {
  goalIds: string[];
  planRevision: string;
}

/**
 * M4: the `targets.review_signed` body, or null when it must not be sent. It needs: at least one M4 goal, EVERY M4 goal
 * signed, a non-empty plan revision, and a run waiting at M4. goalIds are sorted, so one key always carries one payload.
 */
export function targetsSignedEvent(plan: CarePlanView, run: CareModelRunWire | null | undefined): TargetsSignedEvent | null {
  const planRevision = plan.revision;
  if (typeof planRevision !== 'string' || planRevision === '') return null;
  if (!run?.waitingOn?.some((w) => w.nodeId === 'M4')) return null;
  const m4 = plan.goals.filter(isM4Goal);
  if (m4.length === 0 || !m4.every(isSigned)) return null;
  return { goalIds: m4.map((g) => g.id).sort(), planRevision };
}

export interface TrackGroup {
  track: GoalTrack;
  label: string;
  signed: GoalView[];
  proposed: GoalView[];
}

/** A1: goals grouped by track, in a fixed order, empty tracks left out. */
export function groupGoalsByTrack(goals: readonly GoalView[]): TrackGroup[] {
  return TRACK_ORDER.flatMap((track) => {
    const inTrack = goals.filter((g) => trackOf(g) === track);
    if (inTrack.length === 0) return [];
    return [{ track, label: TRACK_LABEL[track], signed: inTrack.filter(isSigned), proposed: inTrack.filter((g) => !isSigned(g)) }];
  });
}

// ---- A2 ----

export const TEAM_ROLES = ['physician', 'allied-health', 'nurse'] as const;

export const CONTRIBUTORS_UNAVAILABLE = 'Care team contributors not recorded on this environment';

/** The description is also the server's duplicate key, so it is the same text every time. */
export const teamTaskDescription = (role: string): string => `Longevity 360 care plan — ${role} follow-up`;

/** A2: the body for the existing POST Tasks, origin "team": no focus, no proposalKey. kind is the closed-enum catch-all. */
export function teamTaskBody(role: string): CreateTeamTaskRequest {
  return {
    origin: 'team',
    kind: 'admin-other',
    description: teamTaskDescription(role),
    performerType: role,
    priority: 'routine',
    when: { anchor: 'none' },
    status: 'requested',
  };
}

/** A2: the person on a row ("You" when the signed-in user owns it), or "Unassigned". Role text alone never names an owner. */
export function memberPerson(m: TeamMemberWire, currentUserId?: string | null): string {
  if (currentUserId && m.ownerUserId === currentUserId) return 'You';
  return m.ownerUserId ?? m.practitionerId ?? 'Unassigned';
}

export const memberStateLabel = (m: TeamMemberWire): 'Accepted' | 'Pending' => (m.accepted ? 'Accepted' : 'Pending');

/** A2: only an open task row that nobody has accepted can be accepted. */
export const canAccept = (m: TeamMemberWire): boolean =>
  m.source === 'task' && !m.accepted && !!m.taskId && typeof m.taskVersion === 'number';

// ---- A3 ----
// The Workflows A3 gate is the only decision point. Nothing here computes a verdict.

export const NO_RUN_MESSAGE = 'No care-model run for this plan';
export const CLEARANCE_SIMULATED_REASON = 'no clearance store exists yet';
export const HOLD_LABEL = 'Hold (recorded by the care-model run)';
export const DEMO_HOLD_NOTE = 'Demo step: raises a real hold on A3 in the dev run';
export const DEMO_HOLD_BODY = { reason: 'Demo: hold raised after signing', sourceVersion: 'A3:demo', ownerRole: 'physician' } as const;
export const APPROVED_MESSAGE = 'Activation approved by the care-model run → R1 (monitoring)';
export const STALE_MESSAGE = 'Activation rejected: the plan, consent, clearance or hold changed since the snapshot';
export const PATIENT_SIMULATED_REASON = 'Patient plan/consent revisions not available here; the run compares its own counters';

export const PATIENT_REVISION_KEYS = ['planRevision', 'consentRevision'] as const;
export type PatientRevisionKey = (typeof PATIENT_REVISION_KEYS)[number];
export const PATIENT_REVISION_LABEL: Record<PatientRevisionKey, string> = {
  planRevision: 'Plan (Patient) revision',
  consentRevision: 'Consent (Patient) revision',
};

/** "Checked against plan X, consent Y", from the `patientRevisions` a 200 routed body carries. */
export const checkedAgainst = (p: PatientRevisions): string => `Checked against plan ${String(p.planRevision)}, consent ${String(p.consentRevision)}`;
/** Patient's activation-snapshot answers this when the patient has no active clinical consent. Kept as is and sent unchanged. */
export const NO_CONSENT_REVISION = 'none';
export const NO_CONSENT_MESSAGE = 'Consent not recorded: activation blocked';
export const NO_CONSENT_REASON = "Record the patient's clinical consent first";
export const CONSENT_REQUIRED_MESSAGE = 'Activation blocked: consent not recorded';
/** The same 409 `consent-required`, but Patient could not be read (no `patientRevisions`): consent is unknown, not absent. */
export const CONSENT_UNKNOWN_MESSAGE = 'Consent could not be checked: activation blocked';
export const NOT_ALLOWED_MESSAGE = 'You are not allowed to approve this activation';
export const hasNoConsent = (p: PatientRevisions | null | undefined): boolean => p?.consentRevision === NO_CONSENT_REVISION;
export const HELD_MESSAGE = 'Activation blocked by an active hold';

export const REVISION_KEYS = ['plan', 'consent', 'clearance', 'hold'] as const;
export type RevisionKey = (typeof REVISION_KEYS)[number];
export const REVISION_LABEL: Record<RevisionKey, string> = { plan: 'Plan', consent: 'Consent', clearance: 'Clearance', hold: 'Hold' };

const definitionKey = (id: string | null | undefined): string | null => id?.trim().split('/').pop() || null;

/** The patient's enrolment for a plan: same definition (bare id or "PlanDefinition/<id>"), active first, else the newest. */
export function pickEnrolment(enrolments: readonly PatientEnrolmentWire[], definitionId: string | null): PatientEnrolmentWire | null {
  const key = definitionKey(definitionId);
  if (!key) return null;
  const same = enrolments.filter((e) => !!e.enrolmentId && definitionKey(e.definitionId) === key);
  const time = (e: PatientEnrolmentWire) => {
    const t = Date.parse(e.enrolledOn ?? '');
    return Number.isNaN(t) ? 0 : t;
  };
  const newestFirst = [...same].sort((a, b) => time(b) - time(a));
  return newestFirst.find((e) => e.status?.toLowerCase() === 'active') ?? newestFirst[0] ?? null;
}

export const isWaitingOnA3 = (run: CareModelRunWire): boolean => run.waitingOn.some((w) => w.nodeId === 'A3');

export const a3LogEntries = (run: CareModelRunWire): RunLogEntryWire[] => run.log.filter((l) => l.nodeId === 'A3');

export const a3Holds = (holds: readonly RunHoldWire[] | null | undefined): RunHoldWire[] =>
  (holds ?? []).filter((h) => h.scopeActivityIds?.includes('A3'));

export const holdIsSimulated = (h: RunHoldWire): boolean => !!h.releaseVerification?.startsWith('SIMULATED');

/** A hold moves one step at a time. */
const HOLD_STEPS = ['raised', 'assigned', 'acknowledged', 'disposition_recorded', 'released'] as const;

/** The `to` states still to run, in order, from the hold's current state to `released`. Released or unknown: none. */
export const holdStepsToRelease = (state: string | null | undefined): string[] => {
  const i = HOLD_STEPS.findIndex((s) => s === state);
  return i < 0 || i === HOLD_STEPS.length - 1 ? [] : HOLD_STEPS.slice(i + 1);
};

/** A NEW key per click: Workflows answers 409 when one key is reused with a different body. */
export const newApproveEventKey = (): string => `ap-panel:A3:approve:${crypto.randomUUID()}`;

export interface RevisionChange {
  key: RevisionKey | PatientRevisionKey;
  label: string;
  was: string;
  now: string;
}

/**
 * The revisions that differ between the snapshot and the current values. The four run revisions always; the two Patient
 * ones only when both sides carry them (a body without `patientRevisions` compares the run ones alone).
 */
export function changedRevisions(
  snapshot: RunRevisions,
  current: RunRevisions,
  patientSnapshot?: PatientRevisions | null,
  patientCurrent?: PatientRevisions | null,
): RevisionChange[] {
  const run: RevisionChange[] = REVISION_KEYS.filter((k) => String(snapshot[k]) !== String(current[k])).map((k) => ({
    key: k,
    label: REVISION_LABEL[k],
    was: String(snapshot[k]),
    now: String(current[k]),
  }));
  if (!patientSnapshot || !patientCurrent) return run;
  const patient: RevisionChange[] = PATIENT_REVISION_KEYS.filter((k) => String(patientSnapshot[k]) !== String(patientCurrent[k])).map((k) => ({
    key: k,
    label: PATIENT_REVISION_LABEL[k],
    was: String(patientSnapshot[k]),
    now: String(patientCurrent[k]),
  }));
  return [...run, ...patient];
}

/**
 * The holds that blocked a 409 held: those the A3 waitingOn entry names in `heldBy` (hold ids) when it names any, else
 * the active ones. Holds come from the body, or from the run when the body has none.
 */
export function blockingHolds(body: A3ConflictWire, runHolds: readonly RunHoldWire[]): RunHoldWire[] {
  const all = a3Holds(body.holds ?? runHolds);
  const heldBy = body.waitingOn?.find((w) => w.nodeId === 'A3')?.heldBy ?? [];
  return heldBy.length > 0 ? all.filter((h) => heldBy.includes(h.holdId)) : all.filter((h) => h.isActive);
}

/** "11 Oct 2026, 08:00 UTC" from an ISO string; the raw text when unreadable. */
export function formatSnapshotTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  return `${d.getUTCDate()} ${months[d.getUTCMonth()]} ${d.getUTCFullYear()}, ${hh}:${mm} UTC`;
}
